/* ============================================================
   sl-particles.js — engine for `particles-by studiolab`
   VERSION:  1.0.0
   CLIENT:   studiolab library (reusable — zero dependencies)

   Renders a Canvas 2D particle field over a host box. Species (shape,
   size, colour, movement, depth, weight) are declared by nested
   `sl-particle` theme blocks, which render hidden definition nodes —
   a theme block cannot read its children's settings in Liquid
   (`block` exposes only id/settings/shopify_attributes/type), so the
   config travels through the DOM as data attributes.

   THE LOOP IS DIRTY-DRIVEN, not an unconditional rAF:
     animate ON                → rAF every frame
     animate OFF + parallax ON → redraw only when the scroll offset moved
     both OFF                  → drawn ONCE (also the reduced-motion state)

   No libraries. GSAP was evaluated and rejected: it can only move the
   canvas element as one rigid plane, so it cannot produce per-particle
   depth, and translating the element exposes edge gaps. Offsetting
   inside the loop has no edge — particles wrap.
   ============================================================ */

(function () {
  'use strict';

  if (window.SLParticles) return;

  var CAP = 400;            // hard ceiling on particle count, any scope
  var BASE_SPEED = 18;      // px/s at Velocidade 100%
  var PARALLAX_BASE = 0.5;  // max fraction of the scroll offset a depth-100 species takes
  var MOBILE = '(max-width: 749px)';
  var DPR_CAP = 2;

  var instances = [];

  /* ── helpers ─────────────────────────────────────────────── */

  /* Deterministic PRNG (mulberry32). The field must not teleport on every
     Theme Editor re-render, and should be reproducible across page loads. */
  function prng(seed) {
    var a = seed >>> 0;
    return function () {
      a |= 0;
      a = (a + 0x6d2b79f5) | 0;
      var t = Math.imul(a ^ (a >>> 15), 1 | a);
      t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
      return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
    };
  }

  function hash(str) {
    var h = 2166136261;
    for (var i = 0; i < str.length; i++) {
      h ^= str.charCodeAt(i);
      h = Math.imul(h, 16777619);
    }
    return h >>> 0;
  }

  function num(v, d) {
    var n = parseFloat(v);
    return isNaN(n) ? d : n;
  }

  function isMobile() {
    return window.matchMedia(MOBILE).matches;
  }

  function reducedMotion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  /* Species live in display:none defs. loading=lazy never fetches them
     (they are never "near the viewport"), so draw() skips the species
     forever. A detached Image() loads regardless of display and becomes
     the canvas drawImage source. */
  function armImage(sp, field) {
    var src = (sp.img.currentSrc || sp.img.getAttribute('src') || sp.img.src || '').trim();
    if (!src) return;

    var probe = new Image();
    probe.decoding = 'async';
    probe.onload = function () {
      sp.img = probe;
      sp.ready = true;
      /* animate ON: rAF already running, next draw() includes this species.
         animate OFF: request() schedules the one-shot redraw. */
      field.request();
    };
    probe.src = src;
  }

  /* ── shape drawing ───────────────────────────────────────── */
  /* Called with the context already translated to the particle centre and
     rotated when the species spins. `s` is the full size (diameter/edge). */
  function drawShape(ctx, shape, s, img) {
    var r = s / 2;

    if (shape === 'image') {
      if (!img) return;
      /* object-fit: contain — `s` is the bounding box, not a stretch target.
         Non-square PNGs were being drawn into s×s and distorting. */
      var nw = img.naturalWidth || img.width || 1;
      var nh = img.naturalHeight || img.height || 1;
      var scale = Math.min(s / nw, s / nh);
      var dw = nw * scale;
      var dh = nh * scale;
      ctx.drawImage(img, -dw / 2, -dh / 2, dw, dh);
      return;
    }

    ctx.beginPath();

    if (shape === 'ring') {
      ctx.arc(0, 0, r, 0, 6.283185307179586);
      ctx.lineWidth = Math.max(1, s * 0.14);
      ctx.stroke();
      return;
    }

    if (shape === 'square') {
      ctx.rect(-r, -r, s, s);
    } else if (shape === 'triangle') {
      ctx.moveTo(0, -r);
      ctx.lineTo(r, r * 0.8);
      ctx.lineTo(-r, r * 0.8);
      ctx.closePath();
    } else if (shape === 'sparkle') {
      /* Four-point twinkle: concave star via quadratics through the centre.
         This is the piece of the original WebGL glitter that survives. */
      ctx.moveTo(0, -r);
      ctx.quadraticCurveTo(0, 0, r, 0);
      ctx.quadraticCurveTo(0, 0, 0, r);
      ctx.quadraticCurveTo(0, 0, -r, 0);
      ctx.quadraticCurveTo(0, 0, 0, -r);
    } else {
      ctx.arc(0, 0, r, 0, 6.283185307179586);
    }

    ctx.fill();
  }

  /* ── species ─────────────────────────────────────────────── */

  function readSpecies(root) {
    var nodes = root.querySelectorAll('[data-sl-particle]');
    var out = [];

    for (var i = 0; i < nodes.length; i++) {
      var d = nodes[i].dataset;
      var img = nodes[i].querySelector('img');
      var shape = d.shape || 'circle';

      /* An image species with no source would draw nothing every frame —
         drop it rather than burn particles on it. */
      if (shape === 'image' && !img) continue;

      out.push({
        shape: shape,
        img: img,
        ready: !img || (img.complete && img.naturalWidth > 0),
        weight: Math.max(0, num(d.weight, 50)),
        size: num(d.size, 8),
        sizeMobile: num(d.sizeMobile, 6),
        sizeVar: num(d.sizeVar, 40) / 100,
        color: d.color || '#ffffff',
        opacity: num(d.opacity, 80) / 100,
        model: d.model || 'deriva',
        angle: num(d.angle, 180),
        speed: num(d.speed, 100) / 100,
        twinkle: d.twinkle === '1',
        rotate: d.rotate === '1',
        rotateSpeed: num(d.rotateSpeed, 100) / 100,
        depth: num(d.depth, 50) / 100
      });
    }

    return out;
  }

  /* ── the field ───────────────────────────────────────────── */

  function Field(root) {
    this.root = root;
    this.d = root.dataset;
    this.scope = this.d.scope || 'parent';
    /* Where the block was actually rendered. The root may be relocated
       (see build), so detachment is tracked against this instead. */
    this.originParent = root.parentElement;
    this.destroyed = false;
    this.raf = 0;
    this.pending = false;
    this.visible = true;
    this.last = 0;
    this.time = 0;
    this.offset = 0;
    this.lastOffset = null;

    this.host = this.resolveHost();
    if (!this.host) return;

    this.species = readSpecies(root);
    /* Empty state cannot be detected in Liquid — `block.blocks` does not
       exist, which is the same constraint that put the species in the DOM
       in the first place. So it is handled here. */
    if (!this.species.length) {
      this.showEmpty();
      return;
    }

    var quiet = reducedMotion();
    this.animate = this.d.animate === '1' && !quiet;
    this.parallax = this.d.parallax === '1' && !quiet;
    this.speedGlobal = num(this.d.speedGlobal, 100) / 100;

    this.mobile = isMobile();
    if (this.count() === 0) return;

    this.build();
  }

  /* Storefront renders nothing when there are no species. In the editor a
     silent nothing reads as a broken block, so the state is made legible. */
  Field.prototype.showEmpty = function () {
    if (!(window.Shopify && window.Shopify.designMode)) return;
    var hint = document.createElement('div');
    hint.className = 'sl-particles__empty';
    hint.textContent = 'Adicione uma partícula';
    this.root.appendChild(hint);
  };

  Field.prototype.resolveHost = function () {
    if (this.scope === 'viewport') return document.body;
    if (this.scope === 'section') return this.root.closest('.shopify-section') || this.root.parentElement;
    return this.root.parentElement;
  };

  Field.prototype.count = function () {
    var q = this.mobile ? num(this.d.quantityMobile, 8) : num(this.d.quantity, 50);
    return Math.min(CAP, Math.max(0, Math.round(q)));
  };

  Field.prototype.parallaxAmount = function () {
    var p = this.mobile ? num(this.d.parallaxMobile, 40) : num(this.d.parallaxIntensity, 50);
    return p / 100;
  };

  Field.prototype.build = function () {
    var self = this;

    if (this.scope === 'viewport') {
      /* `position: fixed` anchors to a transformed/filtered ancestor
         instead of the viewport, and themes apply transforms freely.
         Relocating to <body> is the only reliable fix. */
      if (this.root.parentElement !== document.body) {
        document.body.appendChild(this.root);
        this.moved = true;
      }
    } else {
      /* An absolute root anchors to the nearest POSITIONED ancestor, which
         for scope:section may be an intermediate group rather than the
         section. Reparenting to the host makes the anchor exact and also
         makes the sibling-promotion selector (`> *`) correct. */
      if (this.root.parentElement !== this.host) {
        this.host.appendChild(this.root);
        this.moved = true;
      }

      /* The host must be a positioning context, and its other children
         must be promoted so the canvas can sit behind them. `z-index: -1`
         is NOT an alternative — it would paint the field behind the host's
         own background and silently vanish. */
      this.host.classList.add('sl-particles-host');
      if (getComputedStyle(this.host).position === 'static') {
        this.host.style.position = 'relative';
        this.anchored = true;
      }
      this.promote();
    }

    this.canvas = document.createElement('canvas');
    this.canvas.className = 'sl-particles__canvas';
    this.canvas.setAttribute('aria-hidden', 'true');
    this.ctx = this.canvas.getContext('2d');
    this.root.appendChild(this.canvas);

    this.resize();
    this.seed();

    /* Images decode asynchronously. In static mode nothing would ever
       repaint them, so each pending source triggers one redraw. */
    this.species.forEach(function (sp) {
      if (sp.img && !sp.ready) armImage(sp, self);
    });

    /* threshold: 0 + rootMargin — never a ratio. A fixed threshold never
       fires for a box taller than the viewport (video-by finding). */
    if ('IntersectionObserver' in window) {
      this.io = new IntersectionObserver(function (entries) {
        self.visible = entries[0].isIntersecting;
        if (self.visible) self.start();
        else self.stop();
      }, { threshold: 0, rootMargin: '200px' });
      this.io.observe(this.scope === 'viewport' ? document.documentElement : this.host);
    }

    if ('ResizeObserver' in window && this.scope !== 'viewport') {
      this.ro = new ResizeObserver(function () { self.onResize(); });
      this.ro.observe(this.host);
    }

    this.onWinResize = function () { self.onResize(); };
    window.addEventListener('resize', this.onWinResize, { passive: true });

    if (this.parallax) {
      this.onScroll = function () { self.request(); };
      window.addEventListener('scroll', this.onScroll, { passive: true });
    }

    this.start();
  };

  /* Lift the host's other children above the canvas.
     Done per-child in JS, never as a blanket CSS rule: a rule like
     `.sl-particles-host > * { position: relative }` computes to (0,2,0) and
     beats a sibling's OWN positioning — sl-sticker's
     `.sl-sticker--{id} { position: absolute }` is (0,1,0), as is Horizon's
     `.background-image-container { position: absolute; inset: 0 }` — which
     would collapse them into the flow and destroy their placement.

     So: only children that compute to `static` are positioned, and an
     explicit z-index is always left alone (it is the merchant's choice —
     e.g. sl-sticker's "Camada"). Everything changed here is recorded and
     reverted on destroy. */
  Field.prototype.promote = function () {
    var kids = this.host.children;
    this.promoted = [];

    for (var i = 0; i < kids.length; i++) {
      var el = kids[i];
      if (el === this.root || el.classList.contains('sl-particles')) continue;

      var cs = getComputedStyle(el);
      var rec = { el: el, pos: null, z: null };

      if (cs.position === 'static') {
        rec.pos = el.style.position;
        el.style.position = 'relative';
      }
      /* Only fill in a z-index where there is none. Without this, a
         positioned sibling with z-index:auto would paint below the canvas,
         because the canvas is appended last. */
      if (cs.zIndex === 'auto') {
        rec.z = el.style.zIndex;
        el.style.zIndex = '1';
      }

      if (rec.pos !== null || rec.z !== null) this.promoted.push(rec);
    }
  };

  Field.prototype.demote = function () {
    if (!this.promoted) return;
    this.promoted.forEach(function (r) {
      if (r.pos !== null) r.el.style.position = r.pos;
      if (r.z !== null) r.el.style.zIndex = r.z;
    });
    this.promoted = null;
  };

  Field.prototype.resize = function () {
    var w, h;

    if (this.scope === 'viewport') {
      w = window.innerWidth;
      h = window.innerHeight;
    } else {
      w = this.host.clientWidth;
      h = this.host.clientHeight;
    }

    this.w = Math.max(1, w);
    this.h = Math.max(1, h);

    var dpr = Math.min(DPR_CAP, window.devicePixelRatio || 1);
    this.canvas.width = Math.round(this.w * dpr);
    this.canvas.height = Math.round(this.h * dpr);
    this.canvas.style.width = this.w + 'px';
    this.canvas.style.height = this.h + 'px';
    this.ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
  };

  /* Seeded so the layout is stable across Theme Editor re-renders. */
  Field.prototype.seed = function () {
    var rnd = prng(hash(this.root.id) + num(this.d.seed, 1) * 7919);
    var total = this.count();
    var pool = [];

    var sum = 0;
    this.species.forEach(function (sp) { sum += sp.weight; });
    if (sum <= 0) sum = this.species.length;

    for (var i = 0; i < total; i++) {
      /* Pick a species by weight, using the same seeded stream so the
         mix is reproducible too. */
      var pick = rnd() * sum;
      var idx = 0;
      var acc = 0;
      for (var j = 0; j < this.species.length; j++) {
        acc += this.species[j].weight || 0;
        if (pick <= acc) { idx = j; break; }
        idx = j;
      }

      var sp = this.species[idx];
      var base = this.mobile ? sp.sizeMobile : sp.size;
      /* Compass angle measured from "up", clockwise: 0° up · 90° right ·
         180° down · 270° left. Canvas +y points down, hence the negated
         cosine — do NOT add a 180° offset here, it inverts the default. */
      var rad = sp.angle * Math.PI / 180;

      pool.push({
        sp: idx,
        /* 0–1 base position — kept normalised so a resize rescales
           proportionally instead of reseeding the field. */
        nx: rnd(),
        ny: rnd(),
        size: Math.max(1, base * (1 - sp.sizeVar / 2 + rnd() * sp.sizeVar)),
        alpha: sp.opacity * (0.6 + rnd() * 0.4),
        rot: rnd() * Math.PI * 2,
        rotDir: rnd() < 0.5 ? -1 : 1,
        phase: rnd() * Math.PI * 2,
        phase2: rnd() * Math.PI * 2,
        /* deriva: constant velocity along the compass angle
           (0° = up, 90° = right, 180° = down). flutuação: no net drift. */
        vx: Math.sin(rad) * BASE_SPEED * sp.speed,
        vy: -Math.cos(rad) * BASE_SPEED * sp.speed,
        dx: 0,
        dy: 0
      });
    }

    this.pool = pool;
  };

  Field.prototype.scrollOffset = function () {
    if (!this.parallax) return 0;
    if (this.scope === 'viewport') return window.pageYOffset || 0;
    var r = this.host.getBoundingClientRect();
    /* 0 when the host is centred in the viewport, so the field is neutral
       mid-scroll and travels symmetrically either side. */
    return window.innerHeight / 2 - (r.top + r.height / 2);
  };

  Field.prototype.update = function (dt) {
    var pool = this.pool;
    var species = this.species;
    var g = this.speedGlobal;

    for (var i = 0; i < pool.length; i++) {
      var p = pool[i];
      var sp = species[p.sp];

      if (sp.model === 'flutuacao') {
        /* Sinusoidal wander — no net direction. */
        var amp = p.size * 1.6 * sp.speed * g;
        p.dx = Math.sin(this.time * 0.6 + p.phase) * amp;
        p.dy = Math.cos(this.time * 0.45 + p.phase2) * amp;
      } else {
        p.dx += p.vx * g * dt;
        p.dy += p.vy * g * dt;
      }

      if (sp.rotate) p.rot += p.rotDir * sp.rotateSpeed * g * dt;
    }
  };

  Field.prototype.draw = function () {
    var ctx = this.ctx;
    var W = this.w;
    var H = this.h;

    ctx.clearRect(0, 0, W, H);

    var amount = this.parallaxAmount();
    var pool = this.pool;
    var species = this.species;

    for (var i = 0; i < pool.length; i++) {
      var p = pool[i];
      var sp = species[p.sp];
      if (!sp.ready) continue;

      var s = p.size;
      var pad = s * 2;

      var x = p.nx * W + p.dx;
      var y = p.ny * H + p.dy + this.offset * amount * sp.depth * PARALLAX_BASE;

      /* Wrap through a padded box so nothing pops in at an edge. */
      x = ((x + pad) % (W + pad * 2) + (W + pad * 2)) % (W + pad * 2) - pad;
      y = ((y + pad) % (H + pad * 2) + (H + pad * 2)) % (H + pad * 2) - pad;

      var alpha = p.alpha;
      if (sp.twinkle && this.animate) {
        alpha *= 0.35 + 0.65 * (0.5 + 0.5 * Math.sin(this.time * 2.2 * sp.speed + p.phase));
      }
      /* Fade near the wrap boundary so respawns are never a hard pop. */
      var edge = Math.min(x + pad, y + pad, W + pad - x, H + pad - y);
      if (edge < pad) alpha *= Math.max(0, edge / pad);
      if (alpha <= 0.002) continue;

      ctx.globalAlpha = alpha;
      ctx.fillStyle = sp.color;
      ctx.strokeStyle = sp.color;

      /* Circles and rings never rotate, so the cheap path skips the
         save/translate/rotate/restore entirely. */
      if (!sp.rotate && sp.shape === 'circle') {
        ctx.beginPath();
        ctx.arc(x, y, s / 2, 0, 6.283185307179586);
        ctx.fill();
      } else {
        ctx.save();
        ctx.translate(x, y);
        if (sp.rotate) ctx.rotate(p.rot);
        drawShape(ctx, sp.shape, s, sp.img);
        ctx.restore();
      }
    }

    ctx.globalAlpha = 1;
  };

  /* One redraw, coalesced to the next frame. Used by scroll, resize and
     late-decoding images in the non-animated modes. */
  Field.prototype.request = function () {
    if (this.destroyed || this.animate || this.pending || !this.visible) return;
    var self = this;
    this.pending = true;
    requestAnimationFrame(function () {
      self.pending = false;
      var o = self.scrollOffset();
      if (o === self.lastOffset) return;
      self.lastOffset = o;
      self.offset = o;
      self.draw();
    });
  };

  Field.prototype.frame = function (ts) {
    if (this.destroyed) return;
    var dt = this.last ? Math.min(0.05, (ts - this.last) / 1000) : 0;
    this.last = ts;
    this.time += dt;
    this.offset = this.scrollOffset();
    this.update(dt);
    this.draw();
    var self = this;
    this.raf = requestAnimationFrame(function (t) { self.frame(t); });
  };

  Field.prototype.start = function () {
    if (this.destroyed || !this.visible) return;
    if (this.animate) {
      if (this.raf) return;
      this.last = 0;
      var self = this;
      this.raf = requestAnimationFrame(function (t) { self.frame(t); });
    } else {
      /* Static or parallax-only: draw once now, then only on demand. */
      this.offset = this.scrollOffset();
      this.lastOffset = this.offset;
      this.draw();
    }
  };

  Field.prototype.stop = function () {
    if (this.raf) {
      cancelAnimationFrame(this.raf);
      this.raf = 0;
    }
  };

  Field.prototype.onResize = function () {
    if (this.destroyed) return;
    var wasMobile = this.mobile;
    this.mobile = isMobile();
    this.resize();
    /* Crossing the breakpoint changes both the count and the species
       sizes, so the pool is rebuilt; otherwise positions are normalised
       and rescale on their own. */
    if (wasMobile !== this.mobile) this.seed();
    this.lastOffset = null;
    if (!this.animate) this.start();
  };

  Field.prototype.destroy = function () {
    this.destroyed = true;
    this.stop();
    if (this.io) this.io.disconnect();
    if (this.ro) this.ro.disconnect();
    if (this.onWinResize) window.removeEventListener('resize', this.onWinResize);
    if (this.onScroll) window.removeEventListener('scroll', this.onScroll);
    if (this.canvas && this.canvas.parentNode) this.canvas.parentNode.removeChild(this.canvas);
    if (this.anchored && this.host) this.host.style.position = '';
    if (this.host) this.host.classList.remove('sl-particles-host');
    this.demote();
    /* A relocated root outlives its section, so it must be removed by hand. */
    if (this.moved && this.root.parentNode) this.root.parentNode.removeChild(this.root);
  };

  /* ── registry ────────────────────────────────────────────── */

  function scan() {
    /* Drop instances whose element left the DOM (Theme Editor re-render,
       section unload) before mounting whatever is new. */
    for (var i = instances.length - 1; i >= 0; i--) {
      var anchor = instances[i].originParent || instances[i].root;
      if (!document.contains(anchor)) {
        instances[i].destroy();
        instances.splice(i, 1);
      }
    }

    var nodes = document.querySelectorAll('.sl-particles');
    for (var j = 0; j < nodes.length; j++) {
      if (nodes[j].slParticles) continue;
      var f = new Field(nodes[j]);
      nodes[j].slParticles = f;
      instances.push(f);
    }
  }

  window.SLParticles = { scan: scan, instances: instances };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', scan);
  } else {
    scan();
  }

  document.addEventListener('shopify:section:load', scan);
  document.addEventListener('shopify:block:select', scan);
  document.addEventListener('shopify:section:unload', function () {
    setTimeout(scan, 0);
  });
})();
